import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Independence Day closure announcement.
// Shows every site visit now through the end of July 4th, 2026 Pacific time.
// July is PDT (UTC-7), so end-of-day Pacific = 2026-07-05T07:00:00Z.
const JULY_4TH_END_UTC = Date.UTC(2026, 6, 5, 7, 0, 0); // July 5, 2026 07:00 UTC

const FourthOfJulyPopup = () => {
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (Date.now() <= JULY_4TH_END_UTC) {
      setShowPopup(true);
    }
  }, []);

  const handleClose = () => {
    // No localStorage — shows on every visit during the closure window.
    setShowPopup(false);
  };

  return (
    <AnimatePresence>
      {showPopup && (
        <motion.div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div
            className="relative w-full max-w-sm overflow-hidden shadow-2xl rounded-lg"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 30 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 z-10 bg-black/30 hover:bg-black/50 text-gray-700 hover:text-gray-900 rounded-full p-2 transition-colors"
              aria-label="Close popup"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            {/* Main content area with cream/paper texture background */}
            <div
              className="bg-[#f5f0e1] px-8 py-10 text-center"
              style={{
                backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.85\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' opacity=\'0.08\'/%3E%3C/svg%3E")',
              }}
            >
              {/* Logo */}
              <div className="mb-6">
                <img
                  src="/assets/images/optimized/ThaprachanLogo.webp"
                  alt="Thaprachan Thai"
                  className="h-24 mx-auto"
                />
              </div>

              {/* Closure note — kept small so it doesn't read as "closed today" */}
              <p
                className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                We'll be closed
              </p>

              {/* The date — the primary message */}
              <h2
                className="text-5xl font-bold text-gray-900 leading-none"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                July 4th
              </h2>
            </div>

            {/* Date banner */}
            <div className="bg-gray-900 py-3 px-4">
              <p
                className="text-amber-400 text-sm font-semibold tracking-wider text-center"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                OPEN ALL OTHER DAYS
              </p>
            </div>

            {/* Tap to close hint */}
            <div className="bg-gray-900 pb-3 text-center">
              <span className="text-white/50 text-xs">
                Tap anywhere to close
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FourthOfJulyPopup;
